/**
 * CaptureControls - 撮影コントロールコンポーネント
 */

import type { CameraDevice } from '../../../core/services/camera'

interface CaptureControlsProps {
  isActive: boolean
  isLoading?: boolean
  cameras?: CameraDevice[]
  currentCamera?: CameraDevice | null
  zoomLevel?: number
  torchEnabled?: boolean
  showGrid?: boolean
  showGhost?: boolean
  orientation?: 'portrait' | 'landscape'
  capturedCount?: number
  onCapture: () => void
  onSwitchCamera?: () => void
  onSetZoom?: (level: number) => void
  onToggleTorch?: () => void
  onToggleGrid?: () => void
  onToggleGhost?: () => void
}

export function CaptureControls({
  isActive,
  isLoading = false,
  cameras = [],
  currentCamera,
  zoomLevel = 1,
  torchEnabled = false,
  showGrid = false,
  showGhost = false,
  orientation = 'portrait',
  capturedCount = 0,
  onCapture,
  onSwitchCamera,
  onSetZoom,
  onToggleTorch,
  onToggleGrid,
  onToggleGhost,
}: CaptureControlsProps) {

  // ズームレベルのプリセット - デフォルト値を常に表示
  const getZoomPresets = () => {
    // デフォルトのプリセット
    const defaultPresets = [
      { label: '0.5x', value: 0.5 },
      { label: '1x', value: 1 },
      { label: '2x', value: 2 }
    ]
    
    // カメラの機能が利用可能な場合は拡張
    if (currentCamera?.capabilities?.zoom) {
      const { min, max } = currentCamera.capabilities.zoom
      const presets = []
      
      if (min <= 0.5) presets.push({ label: '0.5x', value: 0.5 })
      presets.push({ label: '1x', value: 1 })
      if (max >= 2) presets.push({ label: '2x', value: 2 })
      if (max >= 5) presets.push({ label: '5x', value: 5 })
      
      return presets
    }
    
    return defaultPresets
  }

  const zoomPresets = getZoomPresets()

  // 横向きレイアウト - 右サイドバー配置（2列グリッド）
  if (orientation === 'landscape') {
    return (
      <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-black/90 via-black/70 to-transparent flex flex-col items-center justify-between py-3" 
        style={{ paddingRight: 'env(safe-area-inset-right)', paddingTop: '60px' }}>
        {/* 上部: ズームコントロール（フレックスレイアウトで3つのボタンに対応） */}
        <div className="flex flex-col gap-2 w-full px-2">
          {zoomPresets.length > 0 && (
            <div className="bg-gray-800/80 rounded-lg p-2">
              <div className={`grid ${zoomPresets.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-1`}>
                {zoomPresets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => onSetZoom?.(preset.value)}
                    className={`px-2 py-2 rounded text-xs font-medium transition-colors min-h-[36px] ${
                      Math.abs(zoomLevel - preset.value) < 0.01
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:text-white bg-gray-700/50'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 中央: その他のコントロール（2列グリッド） */}
        <div className="grid grid-cols-2 gap-2 px-2">
          {/* トーチ */}
          {currentCamera?.capabilities.torch && (
            <button
              onClick={onToggleTorch}
              className={`p-2 rounded-lg transition-colors ${
                torchEnabled ? 'bg-yellow-600 text-white' : 'bg-gray-800/80 text-gray-300 hover:text-white'
              }`}
            >
              <span className="text-lg">⚡</span>
            </button>
          )}

          {/* グリッド */}
          <button
            onClick={onToggleGrid}
            className={`p-2 rounded-lg transition-colors ${
              showGrid ? 'bg-blue-600 text-white' : 'bg-gray-800/80 text-gray-300 hover:text-white'
            }`}
          >
            <span className="text-lg">#</span>
          </button>

          {/* ゴースト */}
          <button
            onClick={onToggleGhost}
            className={`p-2 rounded-lg transition-colors ${
              showGhost ? 'bg-purple-600 text-white' : 'bg-gray-800/80 text-gray-300 hover:text-white'
            }`}
          >
            <span className="text-lg">👻</span>
          </button>

          {/* カメラ切り替え */}
          {cameras.length > 1 && (
            <button
              onClick={onSwitchCamera}
              className="p-2 bg-gray-800/80 rounded-lg text-white hover:bg-gray-700/80 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>

        {/* 下部: 撮影ボタン */}
        <div className="pb-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
          <button
            onClick={onCapture}
            disabled={!isActive || isLoading}
            className="relative"
          >
            <div className={`w-16 h-16 rounded-full border-4 ${
              isActive && !isLoading
                ? 'border-white bg-red-600 hover:bg-red-700'
                : 'border-gray-600 bg-gray-800'
            } transition-all transform active:scale-95`}>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </button>
          
          {/* 撮影済み画像インジケーター */}
          {capturedCount > 0 && (
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-xs text-gray-400">
              {capturedCount}枚
            </div>
          )}
        </div>
      </div>
    )
  }

  // 縦向きレイアウト
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent" 
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* ズームプリセット */}
      {zoomPresets.length > 0 && (
        <div className="flex justify-center gap-2 mb-4 px-4">
          {zoomPresets.map((preset) => (
            <button
              key={preset.value}
              onClick={() => onSetZoom?.(preset.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-w-[60px] ${
                Math.abs(zoomLevel - preset.value) < 0.01
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800/80 text-gray-300 hover:text-white'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
      {/* コントロールバー */}
      <div className="px-4 pb-2">
        <div className="flex justify-center gap-4 mb-4">
          {/* カメラ切り替え */}
          {cameras.length > 1 && (
            <button
              onClick={onSwitchCamera}
              className="p-3 bg-gray-800/80 rounded-full text-white hover:bg-gray-700/80 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}

          {/* トーチ */}
          {currentCamera?.capabilities.torch && (
            <button
              onClick={onToggleTorch}
              className={`p-3 rounded-full transition-colors ${
                torchEnabled 
                  ? 'bg-yellow-600 text-white' 
                  : 'bg-gray-800/80 text-gray-300 hover:text-white'
              }`}
            >
              <span className="text-2xl">⚡</span>
            </button>
          )}

          {/* グリッド */}
          <button
            onClick={onToggleGrid}
            className={`p-3 rounded-full transition-colors ${
              showGrid 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-800/80 text-gray-300 hover:text-white'
            }`}
          >
            <span className="text-2xl">#</span>
          </button>

          {/* ゴースト */}
          <button
            onClick={onToggleGhost}
            className={`p-3 rounded-full transition-colors ${
              showGhost 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-800/80 text-gray-300 hover:text-white'
            }`}
          >
            <span className="text-2xl">👻</span>
          </button>
        </div>
      </div>

      {/* 撮影ボタン */}
      <div className="flex justify-center pb-8">
        <button
          onClick={onCapture}
          disabled={!isActive || isLoading}
          className="relative"
        >
          <div className={`w-20 h-20 rounded-full border-4 ${
            isActive && !isLoading
              ? 'border-white bg-red-600 hover:bg-red-700'
              : 'border-gray-600 bg-gray-800'
          } transition-all transform active:scale-95`}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 border-3 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </button>
      </div>

      {/* ステータス表示 */}
      {capturedCount > 0 && (
        <div className="absolute bottom-2 left-4 text-white text-sm">
          撮影枚数: {capturedCount}
        </div>
      )}
    </div>
  )
}